import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { failedResponses } from '../errors/responses';
import { Category, Manufacturer } from './catalog/types';

const c = initContract();

export enum FinanceModelTypes {
  None,
  FMV,
  FPL
}

export enum PeripheralFinanceModelTypes {
  None,
  FPL
}

export const FinanceModelPcm = z.object({
  region: z.string(),
  value: z.number()
});

export type FinanceModelPcm = z.infer<typeof FinanceModelPcm>;

export const FinanceModelResponse = z.object({
  id: z.string(),
  categoryId: z.string(),
  category: Category,
  manufacturerId: z.string(),
  manufacturer: Manufacturer,
  regionId: z.string(),
  term: z.number(),
  value: z.number(),
  pcmFactor: z.number(),
  financeModelType: z.nativeEnum(FinanceModelTypes),
  isDefault: z.boolean()
});

export type FinanceModelResponse = z.infer<typeof FinanceModelResponse>;

export const FinanceModel = FinanceModelResponse.merge(
  z.object({
    name: z.string(),
    pcms: z.array(FinanceModelPcm)
  })
);

export type FinanceModel = z.infer<typeof FinanceModel>;

export const PeripheralFinanceModelResponse = z.object({
  id: z.string(),
  term: z.number(),
  value: z.number(),
  pcmFactor: z.number(),
  peripheralFinanceModelType: z.nativeEnum(PeripheralFinanceModelTypes),
  regionId: z.string(),
  isDefault: z.boolean()
});

export type PeripheralFinanceModelResponse = z.infer<typeof PeripheralFinanceModelResponse>;

export const PeripheralFinanceModel = PeripheralFinanceModelResponse.merge(
  z.object({
    name: z.string(),
    pcms: z.array(FinanceModelPcm)
  })
);

export type PeripheralFinanceModel = z.infer<typeof PeripheralFinanceModel>;

const FinanceModelRequest = z.object({
  categoryId: z.string(),
  manufacturerId: z.string(),
  regionId: z.string(),
  term: z.number().optional(),
  value: z.number(),
  pcmFactor: z.number().optional(),
  isDefault: z.boolean().optional(),
  financeModelType: z.nativeEnum(FinanceModelTypes)
});

const PeripheralFinanceModelRequest = z.object({
  term: z.number().optional(),
  value: z.number(),
  pcmFactor: z.number().optional(),
  isDefault: z.boolean().optional(),
  regionId: z.string(),
  peripheralFinanceModelType: z.nativeEnum(PeripheralFinanceModelTypes)
});

export default c.router({
  getModels: {
    method: 'GET',
    path: '/financemodels',
    responses: {
      200: z.object({
        items: z.array(FinanceModelResponse)
      }),
      ...failedResponses
    }
  },
  getModel: {
    method: 'GET',
    path: '/financemodels/:id',
    responses: {
      200: FinanceModelResponse,
      ...failedResponses
    }
  },
  createModel: {
    method: 'POST',
    path: '/financemodels',
    responses: {
      200: FinanceModelResponse,
      ...failedResponses
    },
    body: FinanceModelRequest
  },
  createModels: {
    method: 'POST',
    path: '/financemodels/multi',
    responses: {
      200: z.array(FinanceModelResponse),
      ...failedResponses
    },
    body: z.array(FinanceModelRequest)
  },
  updateModel: {
    method: 'PUT',
    path: '/financemodels/:id',
    responses: {
      200: FinanceModelResponse,
      ...failedResponses
    },
    body: FinanceModelRequest
  },
  deleteModel: {
    method: 'DELETE',
    path: '/financemodels/:id',
    responses: {
      200: z.undefined(),
      ...failedResponses
    },
    body: z.undefined()
  },
  getPeripheralModels: {
    method: 'GET',
    path: '/peripheralfinancemodels',
    responses: {
      200: z.object({
        items: z.array(PeripheralFinanceModelResponse)
      }),
      ...failedResponses
    }
  },
  getPeripheralModel: {
    method: 'GET',
    path: '/peripheralfinancemodels/:id',
    responses: {
      200: PeripheralFinanceModelResponse,
      ...failedResponses
    }
  },
  createPeripheralModel: {
    method: 'POST',
    path: '/peripheralfinancemodels',
    responses: {
      200: PeripheralFinanceModelResponse,
      ...failedResponses
    },
    body: PeripheralFinanceModelRequest
  },
  createPeripheralModels: {
    method: 'POST',
    path: '/peripheralfinancemodels/multi',
    responses: {
      200: z.array(PeripheralFinanceModelResponse),
      ...failedResponses
    },
    body: z.array(PeripheralFinanceModelRequest)
  },
  updatePeripheralModel: {
    method: 'PUT',
    path: '/peripheralfinancemodels/:id',
    responses: {
      200: PeripheralFinanceModelResponse,
      ...failedResponses
    },
    body: PeripheralFinanceModelRequest
  },
  deletePeripheralModel: {
    method: 'DELETE',
    path: '/peripheralfinancemodels/:id',
    responses: {
      200: z.undefined(),
      ...failedResponses
    },
    body: z.undefined()
  }
});
